<template>
  <Layout>
    <PageHeader :title="$t('module.catalog')" :items="breadcrumbs" />
    <div class="card">
      <div class="card-body">
        <div class="row mb-3">
          <div class="col-lg-4 mb-2 mb-lg-0">
            <b-input-group>
              <b-form-input
                v-model.trim="searchText"
                type="search"
                :placeholder="$t('label.search')"
                @keyup.enter="applySearch"
              />
              <b-input-group-append
                ><b-button variant="info" @click="applySearch"
                  ><i class="mdi mdi-magnify"></i></b-button
              ></b-input-group-append>
            </b-input-group>
          </div>
          <div class="col-lg-8 d-flex justify-content-lg-end">
            <b-button variant="info" @click="openForm()">
              <i class="mdi mdi-plus mr-1"></i
              >{{ $t("label.createX", [$t("module.catalog")]) }}
            </b-button>
          </div>
        </div>
        <div v-if="!status.list" class="d-flex justify-content-center p-5">
          <b-spinner variant="info" />
        </div>
        <template v-else>
          <b-table
            :items="tableRows"
            :fields="fields"
            show-empty
            responsive
            no-local-sorting
            @sort-changed="changeSort"
          >
            <template #cell(catalogSubcategoryName)="{ item }">{{
              item.catalogSubcategoryName || "-"
            }}</template>
            <template #cell(companyName)="{ item }"
              ><CompanyLink :uuid="item.companyUUID" :name="item.companyName"
            /></template>
            <template #cell(picUserName)="{ item }">{{
              item.picUserName || "-"
            }}</template>
            <template #cell(pdfUrl)="{ item }">
              <a
                v-if="item.pdfUrl"
                :href="item.pdfUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="text-danger"
                v-b-tooltip.hover
                :title="$t('label.openPdf')"
                ><i class="mdi mdi-file-pdf-box font-size-20"></i
              ></a>
              <span v-else>-</span>
            </template>
            <template #cell(isActive)="{ item }"
              ><b-form-checkbox
                :checked="item.isActive"
                switch
                :disabled="!status.save"
                @change="confirmStatus(item, $event)"
              >
                {{ item.isActive ? $t("label.active") : $t("label.inactive") }}
              </b-form-checkbox></template
            >
            <template #cell(actions)="{ item }">
              <a
                href="javascript:void(0);"
                class="text-info"
                @click.stop="openForm(item)"
                ><i class="mdi mdi-pencil-outline font-size-18"></i
              ></a>
              <a
                href="javascript:void(0);"
                class="text-danger ml-2"
                @click.stop="confirmDelete(item)"
                ><i class="mdi mdi-delete-outline font-size-18"></i
              ></a>
            </template>
          </b-table>
          <div
            v-if="pagination.total"
            class="d-flex justify-content-between align-items-center mt-3"
          >
            <small class="text-muted">{{
              $tc("label.results", pagination.total, [pagination.total])
            }}</small>
            <b-pagination
              v-model="currentPage"
              :total-rows="pagination.total"
              :per-page="searchCriteria.pageSize"
              class="mb-0"
              @change="pageNavigation"
            />
          </div>
        </template>
      </div>
    </div>
    <b-modal
      id="catalog-form"
      :title="
        $t(catalog.uuid ? 'label.updateX' : 'label.createX', [
          $t('module.catalog'),
        ])
      "
      size="lg"
      hide-footer
      hide-header-close
      centered
      @hidden="closeForm"
      ><b-container fluid>
        <CatalogForm
          v-if="formVisible"
          :catalog-u-u-i-d="catalog.uuid"
          :search-criteria="searchCriteria"
          @done="closeForm"
        /> </b-container
    ></b-modal>
  </Layout>
</template>

<script>
import { mapState } from "vuex";
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { catalogMethods } from "@/state/api";
import { CATALOG_MODULE } from "@/state/api/catalog.state";
import CatalogForm from "@/views/pages/admin/catalog/form";

const defaultCriteria = () => ({
  pageNumber: 1,
  pageSize: 15,
  searchArray: [],
  sortBy: "created_at",
  orderBy: "desc",
});

export default {
  name: "CatalogListing",
  page: { title: i18n.t("module.catalog") },
  components: { Layout, PageHeader, CatalogForm },
  data() {
    return {
      searchText: "",
      currentPage: 1,
      formVisible: false,
      catalog: { uuid: "" },
      searchCriteria: defaultCriteria(),
      breadcrumbs: [
        { text: i18n.t("module.communication"), href: "/communications" },
        { text: i18n.t("module.catalog"), active: true },
      ],
      fields: [
        { key: "name", label: i18n.t("label.name"), sortable: true },
        {
          key: "catalogSubcategoryName",
          label: i18n.t("label.catalogSubcategory"),
          sortable: false,
        },
        { key: "companyName", label: i18n.t("label.company"), sortable: false },
        { key: "picUserName", label: i18n.t("label.picUser"), sortable: false },
        { key: "pdfUrl", label: i18n.t("label.pdf"), sortable: false },
        { key: "isActive", label: i18n.t("label.status"), sortable: false },
        { key: "actions", label: i18n.t("label.action"), sortable: false },
      ],
    };
  },
  computed: {
    ...mapState(CATALOG_MODULE, ["status"]),
    tableRows() {
      return this.$store.state.api.catalog.list.data || [];
    },
    pagination() {
      return this.$store.state.api.catalog.list.pagination || {};
    },
  },
  created() {
    this.loadCatalogs();
  },
  methods: {
    ...catalogMethods,
    loadCatalogs() {
      return this.getAllCatalog({ filter: this.searchCriteria });
    },
    applySearch() {
      this.currentPage = 1;
      this.searchCriteria = {
        ...this.searchCriteria,
        pageNumber: 1,
        searchArray: this.searchText ? [this.searchText] : [],
      };
      this.loadCatalogs();
    },
    pageNavigation(pageNumber) {
      this.currentPage = pageNumber;
      this.searchCriteria = { ...this.searchCriteria, pageNumber };
      this.loadCatalogs();
    },
    changeSort({ sortBy, sortDesc }) {
      const apiFields = { name: "name" };
      if (!apiFields[sortBy]) return;
      this.currentPage = 1;
      this.searchCriteria = {
        ...this.searchCriteria,
        pageNumber: 1,
        sortBy: apiFields[sortBy],
        orderBy: sortDesc ? "desc" : "asc",
      };
      this.loadCatalogs();
    },
    openForm(catalog = null) {
      this.catalog = { uuid: catalog?.uuid || "" };
      this.formVisible = true;
      this.$nextTick(() => this.$bvModal.show("catalog-form"));
    },
    closeForm() {
      this.$bvModal.hide("catalog-form");
      this.formVisible = false;
      this.catalog = { uuid: "" };
    },
    async confirmStatus(catalog, isActive) {
      const action = this.$t(isActive ? "label.activate" : "label.deactivate");
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t("label.confirmChangeStatusX", [
          action,
          this.$t("module.catalog"),
        ]),
        {
          title: this.$t("label.confirmStatusChange"),
          okTitle: this.$t("label.confirm"),
          okVariant: "info",
          cancelTitle: this.$t("label.cancel"),
          centered: true,
        },
      );
      if (confirmed)
        await this.updateCatalogStatus({
          uuid: catalog.uuid,
          isActive,
          filter: this.searchCriteria,
        });
    },
    async confirmDelete(catalog) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t("label.confirmDeleteX", [this.$t("module.catalog")]),
        {
          title: this.$t("label.confirmDeleteCatalog"),
          okTitle: this.$t("label.delete"),
          okVariant: "danger",
          cancelTitle: this.$t("label.cancel"),
          centered: true,
        },
      );
      if (confirmed) {
        await this.deleteCatalog({
          uuid: catalog.uuid,
          filter: this.searchCriteria,
        });
      }
    },
  },
};
</script>
